import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const APIKeyContext = createContext();

// Storage key for API keys (base64 encoded for basic obfuscation)
const STORAGE_KEY = 'ai-grading-api-keys';

// Default provider configuration
const defaultProviders = {
  deepseek: {
    name: 'DeepSeek',
    endpoint: 'https://api.deepseek.com/v1/chat/completions',
    apiKey: '',
    enabled: false,
    model: 'deepseek-chat'
  },
  openai: {
    name: 'OpenAI',
    endpoint: 'https://api.openai.com/v1/chat/completions',
    apiKey: '',
    enabled: false,
    model: 'gpt-4o'
  },
  anthropic: {
    name: 'Anthropic Claude',
    endpoint: 'https://api.anthropic.com/v1/messages',
    apiKey: '',
    enabled: false,
    model: 'claude-3-5-sonnet-20241022'
  },
  gemini: {
    name: 'Google Gemini',
    endpoint: 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent',
    apiKey: '',
    enabled: false,
    model: 'gemini-2.0-flash'
  },
  groq: {
    name: 'Groq',
    endpoint: 'https://api.groq.com/openai/v1/chat/completions',
    apiKey: '',
    enabled: false,
    model: 'llama-3.3-70b-versatile'
  },
  mistral: {
    name: 'Mistral AI',
    endpoint: 'https://api.mistral.ai/v1/chat/completions',
    apiKey: '',
    enabled: false,
    model: 'mistral-large-latest'
  },
  cohere: {
    name: 'Cohere',
    endpoint: 'https://api.cohere.ai/v1/chat',
    apiKey: '',
    enabled: false,
    model: 'command-r-plus'
  },
  together: {
    name: 'Together AI',
    endpoint: 'https://api.together.xyz/v1/chat/completions',
    apiKey: '',
    enabled: false,
    model: 'meta-llama/Meta-Llama-3.1-70B-Instruct-Turbo'
  },
  openrouter: {
    name: 'OpenRouter',
    endpoint: 'https://openrouter.ai/api/v1/chat/completions',
    apiKey: '',
    enabled: false,
    model: 'anthropic/claude-3.5-sonnet'
  },
  ollama: {
    name: 'Ollama (Local/Remote)',
    endpoint: 'http://localhost:11434',
    apiKey: '', // Not needed for Ollama
    enabled: false,
    model: 'llama3.2',
    isLocal: true
  },
  custom: {
    name: 'Custom OpenAI-Compatible',
    endpoint: '',
    apiKey: '',
    enabled: false,
    model: ''
  }
};

// Store API keys with basic obfuscation
const storeApiKeys = (providers) => {
  try {
    const encoded = btoa(JSON.stringify(providers));
    localStorage.setItem(STORAGE_KEY, encoded);
  } catch (error) {
    console.error('Error storing API keys:', error);
  }
};

// Retrieve API keys
const getStoredApiKeys = () => {
  try {
    const encoded = localStorage.getItem(STORAGE_KEY);
    if (encoded) {
      return JSON.parse(atob(encoded));
    }
  } catch (error) {
    console.error('Error retrieving API keys:', error);
  }
  return null;
};

export const APIKeyProvider = ({ children }) => {
  const [providers, setProviders] = useState(defaultProviders);
  const [isLoading, setIsLoading] = useState(true);
  const [ollamaAvailable, setOllamaAvailable] = useState(false);
  const [ollamaModels, setOllamaModels] = useState([]);

  // Load stored keys on mount
  useEffect(() => {
    const stored = getStoredApiKeys();
    if (stored) {
      setProviders(prev => ({
        ...prev,
        ...stored
      }));
    }
    setIsLoading(false);
  }, []);

  // Check if Ollama is available
  const checkOllama = useCallback(async (endpoint = 'http://localhost:11434') => {
    try {
      const response = await fetch(`${endpoint}/api/tags`, {
        method: 'GET',
        signal: AbortSignal.timeout(3000)
      });

      if (response.ok) {
        const data = await response.json();
        const models = data.models?.map(m => m.name) || [];
        setOllamaModels(models);
        setOllamaAvailable(true);
        return { available: true, models };
      }
    } catch (error) {
      // Ollama not available
    }
    setOllamaAvailable(false);
    setOllamaModels([]);
    return { available: false, models: [] };
  }, []);

  // Check Ollama on mount
  useEffect(() => {
    checkOllama(providers.ollama?.endpoint || 'http://localhost:11434');
  }, []);

  // Update a provider's configuration
  const updateProvider = useCallback((providerKey, updates) => {
    setProviders(prev => {
      const updated = {
        ...prev,
        [providerKey]: {
          ...prev[providerKey],
          ...updates
        }
      };
      storeApiKeys(updated);
      return updated;
    });
  }, []);

  // Test a provider's connection
  const testProvider = useCallback(async (providerKey) => {
    const provider = providers[providerKey];
    if (!provider) return { success: false, error: 'Provider not found' };

    try {
      if (providerKey === 'ollama') {
        const result = await checkOllama(provider.endpoint);
        if (result.available) {
          return { success: true, message: `Connected! ${result.models.length} models available.` };
        }
        return { success: false, error: 'Ollama not responding at this endpoint' };
      }

      // For API-based providers, make a simple test request
      if (!provider.apiKey && providerKey !== 'ollama') {
        return { success: false, error: 'API key required' };
      }

      // Test with a minimal request
      const testPrompt = 'Say "OK" if you can read this.';

      let requestBody, headers;

      if (providerKey === 'gemini') {
        headers = { 'Content-Type': 'application/json' };
        requestBody = {
          contents: [{ parts: [{ text: testPrompt }] }],
          generationConfig: { maxOutputTokens: 10 }
        };
        const response = await fetch(`${provider.endpoint}?key=${provider.apiKey}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
          signal: AbortSignal.timeout(10000)
        });

        if (response.ok) {
          return { success: true, message: 'Connection successful!' };
        }
        const error = await response.json();
        return { success: false, error: error.error?.message || 'Connection failed' };
      }

      // Handle Anthropic separately
      if (providerKey === 'anthropic') {
        headers = {
          'Content-Type': 'application/json',
          'x-api-key': provider.apiKey,
          'anthropic-version': '2023-06-01'
        };
        requestBody = {
          model: provider.model || 'claude-3-haiku-20240307',
          max_tokens: 10,
          messages: [{ role: 'user', content: testPrompt }]
        };

        const response = await fetch(provider.endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
          signal: AbortSignal.timeout(10000)
        });

        if (response.ok) {
          return { success: true, message: 'Connection successful!' };
        }
        const error = await response.json();
        return { success: false, error: error.error?.message || 'Connection failed' };
      }

      // Handle Cohere separately
      if (providerKey === 'cohere') {
        headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${provider.apiKey}`
        };
        requestBody = {
          model: provider.model || 'command-r',
          message: testPrompt
        };

        const response = await fetch(provider.endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody),
          signal: AbortSignal.timeout(10000)
        });

        if (response.ok) {
          return { success: true, message: 'Connection successful!' };
        }
        const error = await response.json();
        return { success: false, error: error.message || 'Connection failed' };
      }

      // OpenAI-compatible providers (DeepSeek, OpenAI, Groq, Mistral, Together, OpenRouter, Custom)
      headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${provider.apiKey}`
      };

      // OpenRouter requires additional headers
      if (providerKey === 'openrouter') {
        headers['HTTP-Referer'] = window.location.origin;
        headers['X-Title'] = 'AI Grading Agent';
      }

      requestBody = {
        model: provider.model || 'gpt-3.5-turbo',
        messages: [{ role: 'user', content: testPrompt }],
        max_tokens: 10
      };

      const response = await fetch(provider.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody),
        signal: AbortSignal.timeout(10000)
      });

      if (response.ok) {
        return { success: true, message: 'Connection successful!' };
      }
      const error = await response.json();
      return { success: false, error: error.error?.message || 'Connection failed' };

    } catch (error) {
      return { success: false, error: error.message || 'Connection timeout' };
    }
  }, [providers, checkOllama]);

  // Get all enabled providers
  const getEnabledProviders = useCallback(() => {
    return Object.entries(providers)
      .filter(([_, provider]) => provider.enabled && (provider.apiKey || provider.isLocal))
      .map(([key, provider]) => ({ key, ...provider }));
  }, [providers]);

  // Check if any provider is configured
  const hasConfiguredProvider = useCallback(() => {
    return Object.values(providers).some(
      provider => provider.enabled && (provider.apiKey || provider.isLocal)
    );
  }, [providers]);

  // Get API keys in format expected by GradingAgent
  const getApiKeysForGrading = useCallback(() => {
    return {
      deepseek: providers.deepseek.enabled ? providers.deepseek.apiKey : null,
      openai: providers.openai.enabled ? providers.openai.apiKey : null,
      anthropic: providers.anthropic.enabled ? providers.anthropic.apiKey : null,
      gemini: providers.gemini.enabled ? providers.gemini.apiKey : null,
      groq: providers.groq.enabled ? providers.groq.apiKey : null,
      mistral: providers.mistral.enabled ? providers.mistral.apiKey : null,
      cohere: providers.cohere.enabled ? providers.cohere.apiKey : null,
      together: providers.together.enabled ? providers.together.apiKey : null,
      openrouter: providers.openrouter.enabled ? providers.openrouter.apiKey : null,
      ollama: providers.ollama.enabled ? {
        endpoint: providers.ollama.endpoint,
        model: providers.ollama.model
      } : null,
      custom: providers.custom.enabled ? {
        endpoint: providers.custom.endpoint,
        apiKey: providers.custom.apiKey,
        model: providers.custom.model
      } : null
    };
  }, [providers]);

  // Get preferred provider (first enabled one)
  const getPreferredProvider = useCallback(() => {
    const order = ['ollama', 'groq', 'deepseek', 'together', 'mistral', 'openrouter', 'cohere', 'openai', 'anthropic', 'gemini', 'custom'];
    for (const key of order) {
      if (providers[key].enabled && (providers[key].apiKey || providers[key].isLocal)) {
        return key;
      }
    }
    return null;
  }, [providers]);

  // Clear all stored keys
  const clearAllKeys = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    setProviders(defaultProviders);
  }, []);

  const value = {
    providers,
    isLoading,
    ollamaAvailable,
    ollamaModels,
    updateProvider,
    testProvider,
    checkOllama,
    getEnabledProviders,
    hasConfiguredProvider,
    getApiKeysForGrading,
    getPreferredProvider,
    clearAllKeys
  };

  return (
    <APIKeyContext.Provider value={value}>
      {children}
    </APIKeyContext.Provider>
  );
};

export const useAPIKeys = () => {
  const context = useContext(APIKeyContext);
  if (!context) {
    throw new Error('useAPIKeys must be used within an APIKeyProvider');
  }
  return context;
};

export default APIKeyContext;
